// src/AuthScreen.js — signed-out screens: Log in / Create account, and "Check your email" for new password
// accounts. Google comes first (no password, and the calendar can connect in the same step). Approved mockup:
// the "FriSo Login" artifact (2026-10-12).
import { useState, useEffect } from "react";
import { auth, db, functions } from "./firebase";
import { httpsCallable } from "firebase/functions";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import BrandLogo from "./BrandLogo";

const PRIVACY_URL = "https://frisoapp.com/privacy";

// A password account must confirm its email before it can see anything shared (firestore.rules check
// email_verified). Google accounts arrive verified.
export const needsEmailCheck = (u) => !!u && !u.emailVerified && u.providerData.some((p) => p.providerId === "password");

const MESSAGES = {
  "auth/invalid-credential": "That email and password don’t match. Try again, or reset your password.",
  "auth/wrong-password": "That email and password don’t match. Try again, or reset your password.",
  "auth/user-not-found": "That email and password don’t match. Try again, or reset your password.",
  "auth/invalid-email": "That doesn’t look like an email address.",
  "auth/missing-password": "Enter your password.",
  "auth/email-already-in-use": "There’s already an account with this email. Log in instead, or use Continue with Google if that’s how you joined.",
  "auth/weak-password": "Use at least 8 characters.",
  "auth/too-many-requests": "Too many tries. Wait a minute and try again.",
  "auth/network-request-failed": "Couldn’t reach FriSo. Check your connection.",
};
export const authMessage = (err) => MESSAGES[err?.code] || "Something went wrong. Please try again.";

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export const ErrorLine = ({ id, children }) => (
  <p id={id} className="auth-error" role="alert">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.5M12 16.5v.01" />
    </svg>
    {children}
  </p>
);

export function Shell({ children, hero = true }) {
  return (
    <div className="auth-page">
      <a className="auth-logo" href="https://frisoapp.com" aria-label="FriSo home"><BrandLogo /></a>
      <div className="auth-main">
        {hero && (
          <div className="auth-hero">
            <h1>Make time for your people.</h1>
            <p>One calendar for your friends and family. See who’s around, plan together, and keep private things private.</p>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export default function AuthScreen({ onGoogle }) {
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const signup = mode === "signup";

  const switchMode = (m) => { setMode(m); setError(""); setInfo(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    if (signup) {
      if (!firstName.trim()) return setError("Enter your first name.");
      if (password.length < 8) return setError("Use at least 8 characters.");
    }
    setBusy(true);
    try {
      if (signup) {
        const { user } = await createUserWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
        const first = firstName.trim(), last = lastName.trim();
        // The name pod members see (and get in their Contacts) once they share a pod — see acceptInvitation
        await Promise.all([
          updateProfile(user, { displayName: `${first} ${last}`.trim() }),
          setDoc(doc(db, "profiles", user.uid), { firstName: first, lastName: last, email: user.email }),
        ]);
        // Our own email (functions sendVerificationEmail) so the link is on app.frisoapp.com. If it fails, the
        // Check-your-email screen that follows has a Resend button.
        await httpsCallable(functions, "sendVerificationEmail")().catch((e) => console.error("Confirm email not sent", e));
      } else {
        await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      }
    } catch (err) {
      setError(authMessage(err));
      setBusy(false);
    }
  };

  const forgot = async () => {
    setError(""); setInfo("");
    if (!email.trim()) return setError("Enter your email above, then tap Forgot password.");
    try {
      await httpsCallable(functions, "sendPasswordReset")({ email: email.trim().toLowerCase() });
    } catch (err) {
      if (err.code === "functions/invalid-argument") return setError(authMessage({ code: "auth/invalid-email" }));
      // Any other error: same answer, so this can't be used to find out who has an account
    }
    setInfo(`If there’s an account for ${email.trim()}, we sent a link to reset its password.`);
  };

  const google = async () => {
    setError(""); setInfo("");
    const err = await onGoogle();
    if (err) setError(authMessage(err));
  };

  return (
    <Shell>
      <form className="auth-card" onSubmit={submit} noValidate>
        <div className="auth-tabs" role="tablist" aria-label="Log in or create an account">
          <button type="button" role="tab" aria-selected={!signup} className={`auth-tab${!signup ? " auth-tab--on" : ""}`} onClick={() => switchMode("login")}>Log in</button>
          <button type="button" role="tab" aria-selected={signup} className={`auth-tab${signup ? " auth-tab--on" : ""}`} onClick={() => switchMode("signup")}>Create account</button>
        </div>

        <div className="auth-heading">
          <h2>{signup ? "Join FriSo" : "Welcome back"}</h2>
          <p>{signup ? "Free, and it takes a minute." : "See what your pods are up to."}</p>
        </div>

        <button type="button" className="btn btn--google btn--full auth-btn" onClick={google}>
          <GoogleIcon />
          Continue with Google
        </button>
        {!signup && <p className="auth-note">No password to remember, and your Google Calendar can connect in the same step.</p>}

        <div className="auth-divider"><span>or use email</span></div>

        {signup && (
          <div className="auth-row">
            <label className="auth-field">
              <span>First name</span>
              <input className="form-input" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </label>
            <label className="auth-field">
              <span>Last name</span>
              <input className="form-input" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </label>
          </div>
        )}
        <label className="auth-field">
          <span>Email</span>
          <input className="form-input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <div className="auth-field">
          <div className="auth-field__top">
            <label htmlFor="auth-pw">Password</label>
            {!signup && <button type="button" className="auth-linkbtn" onClick={forgot}>Forgot password?</button>}
          </div>
          <input
            id="auth-pw"
            className={`form-input${error ? " form-input--error" : ""}`}
            type="password"
            autoComplete={signup ? "new-password" : "current-password"}
            aria-invalid={!!error}
            aria-describedby={error ? "auth-err" : signup ? "auth-pw-hint" : undefined}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {signup && !error && <span id="auth-pw-hint" className="auth-hint">At least 8 characters.</span>}
        </div>
        {error && <ErrorLine id="auth-err">{error}</ErrorLine>}
        {info && <p className="auth-info" role="status">{info}</p>}

        <button type="submit" className="btn btn--primary btn--full auth-btn" disabled={busy}>
          {busy ? "One moment…" : signup ? "Create account" : "Log in"}
        </button>

        <p className="auth-note">
          {signup ? <>We’ll email you a link to confirm your address. By joining you agree to our <a href={PRIVACY_URL}>Privacy policy</a>.</>
                  : <a href={PRIVACY_URL}>Privacy policy</a>}
        </p>
      </form>
    </Shell>
  );
}

// Shown to a password account until it confirms its email. onConfirmed runs once the account reads as verified.
export function CheckEmail({ user, onConfirmed }) {
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const check = async (quiet) => {
    setError("");
    try {
      await user.reload();
      if (auth.currentUser?.emailVerified) {
        await auth.currentUser.getIdToken(true); // so the database rules see the verified email
        onConfirmed();
      } else if (!quiet) setError("We don’t see it confirmed yet. Tap the link in the email, then try again.");
    } catch (err) {
      if (!quiet) setError(authMessage(err));
    }
  };

  // Coming back to this tab after tapping the link is enough
  useEffect(() => {
    const onFocus = () => check(true);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resend = async () => {
    setError(""); setMsg("");
    try {
      await httpsCallable(functions, "sendVerificationEmail")();
      setMsg("Sent. It can take a minute to arrive.");
    } catch (err) {
      setError(err.code === "functions/resource-exhausted" ? err.message : "Couldn't send the email. Please try again.");
    }
  };

  return (
    <Shell hero={false}>
      <div className="auth-card auth-card--center">
        <div className="auth-mailicon" aria-hidden="true">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M3.5 6.5l8.5 6.5 8.5-6.5" />
          </svg>
        </div>
        <div className="auth-heading">
          <h2>Check your email</h2>
          <p>We sent a link to <strong>{user.email}</strong>. Tap it to confirm it’s you, then come back here.</p>
        </div>
        {error && <ErrorLine>{error}</ErrorLine>}
        {msg && <p className="auth-info" role="status">{msg}</p>}
        <button type="button" className="btn btn--primary btn--full auth-btn" onClick={() => check(false)}>I’ve confirmed it</button>
        <button type="button" className="btn btn--secondary btn--full auth-btn" onClick={resend}>Send it again</button>
        <p className="auth-note">
          Not in your inbox? Check spam, or <button type="button" className="auth-linkbtn" onClick={() => auth.signOut()}>use a different email</button>.
        </p>
        <p className="auth-note">Confirming keeps someone else from signing up with your address and seeing plans shared with you.</p>
      </div>
    </Shell>
  );
}
